.sitesCardHeader {
	@apply -yst-mt-6 -yst-mx-6 yst-border-b yst-border-slate-200 yst-p-6 yst-space-y-4;
}

.cardContent {
	@apply yst-space-y-4;
}

.alertBody {
	// Keep the dismiss button in flow beside the text so it always reserves its own space,
	// even when the message wraps on narrow screens.
	@apply yst-flex yst-items-start yst-justify-between yst-gap-3;
}

button.alertDismissButton {
	@apply yst-shrink-0 yst-text-blue-500 hover:yst-text-blue-700;
}

.alertDismissIcon {
	@apply yst-w-5 yst-h-5;
}

.tableWrapper {
	// Compensate for border radius making the table overflow.
	@apply yst-p-0.5 yst-overflow-x-auto;
	// Query container so the table reflows to the card's own width, not the viewport
	// (consistent with the homepage grid, which is also container-query driven).
	container: sitesTable / inline-size;

	table {
		// Fixed layout keeps column widths stable across pages regardless of site-name length.
		@apply yst-w-full;
		table-layout: fixed;
	}
}

// Below this width the three fixed secondary columns (128 + 144 + 96 = 368px) no longer
// leave room for the Site URL column, so instead of side-scrolling we drop the table
// layout and stack each site into a compact list item: the site name as the heading, then
// each value inline with its column title, and the action below.
//
//   site-name.example
//   Subscription status: Inactive
//   Subscription ends on: —
//   [ Fix ]
//
// Keyed to the card width via the container query above.
@container sitesTable (max-width: 520px) {
	// Blockify the table subtree. The Yoast Tailwind preset sets `important: true`, so the
	// ui-library's utility classes force `display: table-*` with `!important`; collapsing the
	// table therefore needs `!important` on our own display overrides too (same note as
	// OrdersPageStyles.module.scss). We use the `@apply … #{!important}` escape hatch.
	.tableWrapper table,
	.tableWrapper tbody,
	.tableWrapper tbody tr,
	.siteRow > td {
		@apply yst-block #{!important};
	}

	.tableWrapper table {
		@apply yst-w-full;
		table-layout: auto;
	}

	// No table header row on mobile — the column titles move above each value below.
	.tableWrapper thead {
		@apply yst-sr-only;
	}

	// Each site is a stacked, hairline-separated list item.
	.siteRow {
		@apply yst-py-3 yst-border-b yst-border-slate-200;
	}

	// Drop only the tabular VERTICAL padding — the ui-library's `.yst-table-cell` `py-4`
	// (32px/cell) was the real source of the vertical bulk, not our margins. Keep a little
	// horizontal padding so the text isn't jammed against the card border. Needs
	// `!important` to beat the ui-library utilities.
	.siteRow > td {
		@apply yst-py-0 yst-px-3 #{!important};
		@apply yst-border-0;
	}

	// The site name is the item heading: bold, full width, truncates when long.
	.siteNameHeadingCell {
		@apply yst-min-w-0 yst-mb-1.5;
	}

	.siteName {
		@apply yst-text-base yst-font-semibold yst-leading-tight;
	}

	.siteRow .rowNumber {
		@apply yst-hidden;
	}

	// Value fields: column title inline with its value ("Subscription status: Inactive"),
	// one line each, so the item stays compact.
	.siteRow > td[data-label] {
		@apply yst-mt-1.5 yst-text-sm yst-leading-snug;

		&::before {
			content: attr(data-label) ": ";
			@apply yst-font-medium yst-text-slate-500;
		}
	}

	// Status value is colour-coded (green = active, red = needs fixing); its bold label stays muted.
	// The colour needs `!important` to beat the ui-library's high-specificity
	// `.yst-root .yst-table-wrapper table .yst-table-cell` text colour.
	td.statusCell {
		@apply yst-text-green-600 #{!important};
		@apply yst-font-medium;
	}

	td.statusCell.inactive {
		@apply yst-text-red-600 #{!important};
	}

	// The action button sits below the values, left-aligned.
	td.actionsCell {
		@apply yst-flex #{!important};
		@apply yst-w-auto yst-justify-start yst-px-3;

		// Desktop keeps the borderless tertiary link; on mobile the button reads as a stray
		// pink word, so give it an outline here only. The ui-library button uses a `ring`
		// (box-shadow) for its outline and never sets `border`, so a plain `border` applies
		// cleanly without fighting the tertiary variant's layered `!important` ring reset.
		// `mt-2` (8px) gives it a little breathing room above.
		button {
			@apply yst-mt-2 yst-border yst-border-slate-300;
		}
	}

	// Search "no results" spans the full width as plain text.
	td.noResults {
		@apply yst-block #{!important};
		@apply yst-text-left yst-py-3;
	}
}

// Fixed widths for the secondary columns so the Site URL column absorbs the
// remaining (largest) width; headers may wrap to two lines, which is fine.
.colStatus {
	@apply yst-w-32;
}

.colEndsOn {
	@apply yst-w-36;
}

.siteNameCell {
	@apply yst-flex yst-items-center yst-min-w-0;
}

.rowNumber {
	@apply yst-text-slate-500 yst-pr-2 yst-shrink-0;
}

.siteName {
	@apply yst-font-medium yst-text-slate-900 yst-truncate;
}

td.inactive {
	// Greyed out, but slate-500 is the lightest shade that keeps sufficient contrast.
	@apply yst-text-slate-500;
}

.actionsCell {
	@apply yst-text-right yst-w-24;
}

td.noResults {
	@apply yst-italic;
}

.paginationFooter {
	@apply yst-flex yst-flex-wrap yst-items-center yst-justify-end yst-gap-x-4 yst-gap-y-2;
}

.showingResults {
	@apply yst-text-sm yst-text-slate-600;
}

.noSites {
	@apply yst-italic;
}

button.addSiteButton {
	@apply yst-w-full yst-justify-center yst-gap-1;
}

.addSiteButtonIcon {
	@apply yst-w-4 yst-h-4;
}
